import * as RadixTooltip from '@radix-ui/react-tooltip';
import cn from 'classnames';

import { themeClasses } from '../../../css/utils';

import styles from './Tooltip.module.css';
export interface TooltipRootProps {
  children: React.ReactNode;
}

export interface TooltipTriggerProps
  extends React.ComponentPropsWithoutRef<typeof RadixTooltip.Trigger> {
  children: React.ReactNode;
  asChild?: boolean;
}

export interface TooltipContentProps {
  /**
   * Offset distance from the trigger element
   */
  sideOffset?: number;
  /**
   * Specifies the side where the tooltip will appear (top, right, bottom, left)
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * The content to display inside the tooltip
   */
  children: React.ReactNode;
  /**
   * Whether to show the arrow
   */
  showArrow?: boolean;
  /**
   * The distance in pixels from the boundary edges where collision detection should occur
   */
  collisionPadding?: number;
}

const TooltipRoot: React.FC<TooltipRootProps> = ({ children }) => {
  return <RadixTooltip.Root>{children}</RadixTooltip.Root>;
};

const TooltipTrigger: React.FC<TooltipTriggerProps> = ({
  children,
  asChild,
  ...props
}) => {
  return (
    <RadixTooltip.Trigger
      aria-describedby="tooltip-content"
      asChild={asChild}
      className={asChild ? undefined : styles['tooltip-trigger-button']}
      {...props}
    >
      {children}
    </RadixTooltip.Trigger>
  );
};

const TooltipContent: React.FC<TooltipContentProps> = ({
  sideOffset = 8,
  side = 'top',
  children,
  showArrow = false,
}) => {
  return (
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        className={cn(
          styles['tooltip-content'],
          // IMPORTANT: We are forcing the light theme here in order to get the dark background color
          // variation. This is because some components are still using the old Tooltips and we cannot
          // enable the other variation yet because it would create inconsistencies in styling.
          themeClasses.light
        )}
        collisionPadding={6}
        side={side}
        sideOffset={sideOffset}
        sticky="always"
      >
        {children}
        {showArrow && (
          <RadixTooltip.Arrow className={styles['tooltip-arrow']} />
        )}
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
};

export const Tooltip = {
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Content: TooltipContent,
  Provider: RadixTooltip.Provider,
};
